// The passenger card's clock: the CONSOLE's language (not the browser's) and the station's
// local time, 24 h — «31 oct · 23:13» in Spanish, «Oct 31 · 23:13» in English. `moment` drops
// the date when it is today at that station (a deadline in two hours reads «14:50»).

import { airportIanaZone } from '@/lib/disruptions/airport-tz';

export interface CardClock {
  /** «23:13». */
  time: (iso: string, airport?: string | null) => string;
  /** «31 oct · 23:13». */
  when: (iso: string, airport?: string | null) => string;
  /** `time` when it is today at the station, else `when`. */
  moment: (iso: string, airport?: string | null) => string;
  /** «31 oct» — the day alone. */
  date: (iso: string, airport?: string | null) => string;
  /** It is today at the station. */
  isToday: (iso: string, airport?: string | null) => boolean;
}

export function cardClock(lng: string, defaultAirport: string, now: number = Date.now()): CardClock {
  const parts = (iso: string, airport?: string | null): { day: string; time: string } | null => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const timeZone = airportIanaZone(airport ?? defaultAirport);
    try {
      return {
        day: new Intl.DateTimeFormat(lng, { timeZone, day: 'numeric', month: 'short' }).format(t),
        time: new Intl.DateTimeFormat(lng, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(t),
      };
    } catch {
      return null;
    }
  };
  const today = (airport?: string | null): string | undefined => parts(new Date(now).toISOString(), airport)?.day;
  return {
    time: (iso, airport) => parts(iso, airport)?.time ?? '—',
    when: (iso, airport) => {
      const p = parts(iso, airport);
      return p ? `${p.day} · ${p.time}` : '—';
    },
    moment: (iso, airport) => {
      const p = parts(iso, airport);
      if (!p) return '—';
      return p.day === today(airport) ? p.time : `${p.day} · ${p.time}`;
    },
    date: (iso, airport) => parts(iso, airport)?.day ?? '—',
    isToday: (iso, airport) => {
      const p = parts(iso, airport);
      return !!p && p.day === today(airport);
    },
  };
}
